import type { Organization, OrganizationMember } from '@/types';
import type { Department } from '@/hooks/useDepartments';
import type { BootstrapSubscriptionReadModelResponse, BootstrapSupplierLimitReadModelResponse } from '@/lib/readModels';

const ORGANIZATION_CACHE_PREFIX = 'organization-context-cache:';

/**
 * Schema version for the persisted organization context cache (ENT-1495).
 *
 * v2 invalidated caches written by app versions that pre-date the ENT-1303/1304
 * per-actor MSP scoping.
 *
 * Bumped to 3 for ENT-2659: `get_organization_context_read_model` now scopes
 * `mspCustomerOrgs` to the single active MSP org (retiring the client-side
 * scope filter). A v2 payload written before this change may hold the broader
 * cross-MSP union, so hydrating it as a first-paint source — now that no
 * client-side filter masks it — would briefly render other MSP accounts'
 * customers on `/team`, `/customers`, and MSP Insights before the fresh
 * bootstrap fetch lands. Rejecting v2 forces a re-fetch from the scoped RPC.
 *
 * Increment whenever the persisted shape changes in a way that older
 * snapshots can no longer be trusted as a first-paint source of truth.
 */
export const ORGANIZATION_CONTEXT_CACHE_SCHEMA_VERSION = 3;

export interface OrganizationContextCacheValue {
  /** Persisted schema version — see ORGANIZATION_CONTEXT_CACHE_SCHEMA_VERSION. */
  schemaVersion: typeof ORGANIZATION_CONTEXT_CACHE_SCHEMA_VERSION;
  userId: string;
  organization: Organization | null;
  membership: OrganizationMember | null;
  organizations: Organization[];
  mspCustomerOrgs: Organization[];
  departments: Department[];
  isMspOrg: boolean;
  isMspContext: boolean;
  mspHomeOrgId: string | null;
  subscription: BootstrapSubscriptionReadModelResponse | null;
  supplierLimit?: BootstrapSupplierLimitReadModelResponse | null;
}

/**
 * Input accepted by `writeOrganizationContextCache`. Callers do not have to
 * supply `schemaVersion` themselves — the writer stamps the current version
 * on every write so the field can never be omitted or lag the source code.
 */
export type OrganizationContextCacheInput = Omit<OrganizationContextCacheValue, 'schemaVersion'> & {
  schemaVersion?: typeof ORGANIZATION_CONTEXT_CACHE_SCHEMA_VERSION;
};

function getCacheKey(userId: string) {
  return `${ORGANIZATION_CACHE_PREFIX}${userId}`;
}

export function readOrganizationContextCache(
  storage: Pick<Storage, 'getItem'>,
  userId: string,
): OrganizationContextCacheValue | null {
  const raw = storage.getItem(getCacheKey(userId));
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<OrganizationContextCacheValue>;
    if (parsed.userId !== userId) {
      return null;
    }
    if (parsed.schemaVersion !== ORGANIZATION_CONTEXT_CACHE_SCHEMA_VERSION) {
      // Treat any older or missing schema version as a cache miss so callers
      // re-fetch from the canonical bootstrap RPC. This is what fixes the
      // `/customers` first-paint flash for users carrying pre-ENT-1303/1304
      // caches in localStorage.
      return null;
    }

    return parsed as OrganizationContextCacheValue;
  } catch {
    return null;
  }
}

/**
 * ENT-2659: `mspCustomerOrgs` is org-scoped, but the bootstrap cache-write path
 * re-persists the cache for the (possibly new) active org while the fresh scoped
 * `mspCustomerOrgs` is still loading — so it can only reuse a previously-cached
 * list. Carrying that list forward across an org switch would persist the PRIOR
 * MSP's customers under the new org; if the deferred scoped read then fails or the
 * browser reloads in that window, the next load hydrates a schema-current cache
 * with the new org but the old customers, and the now-unfiltered `/team` selector
 * renders them. Only reuse the cached list when it belongs to the SAME active org;
 * otherwise persist `[]` (matches the reducer's clear-on-org-change).
 */
export function carryForwardCachedMspCustomerOrgs(
  cached: OrganizationContextCacheValue | null,
  activeOrgId: string,
): Organization[] {
  if (cached && cached.organization?.id === activeOrgId) {
    return cached.mspCustomerOrgs ?? [];
  }
  return [];
}

export function writeOrganizationContextCache(
  storage: Pick<Storage, 'setItem'>,
  value: OrganizationContextCacheInput,
) {
  const stamped: OrganizationContextCacheValue = {
    ...value,
    schemaVersion: ORGANIZATION_CONTEXT_CACHE_SCHEMA_VERSION,
  };
  storage.setItem(getCacheKey(stamped.userId), JSON.stringify(stamped));
}

export function clearOrganizationContextCache(
  storage: Pick<Storage, 'removeItem'>,
  userId: string,
) {
  storage.removeItem(getCacheKey(userId));
}
